/**
 * stitchParser.js
 * Client-side parsers for embroidery binary formats.
 * Converts the raw bytes returned by the Cloud Run service into an array of
 * stitch objects that the canvas renderer can draw as real needle paths.
 *
 * Supported formats:  DST (Tajima)  ·  PES (Brother)
 *
 * Stitch object shape:
 *   { x: number, y: number, colorIndex: number, jump: boolean }
 *   x/y are in native file units (0.1 mm for DST).
 */

// ─── Default thread colour palette ────────────────────────────────────────────
// DST files carry no colour table; we cycle through this palette per
// color-change command.  PES embeds actual RGB values which are used directly.
export const DEFAULT_THREAD_COLORS = [
  '#1a1a2e', // 0  deep navy
  '#e63946', // 1  red
  '#2a9d8f', // 2  teal
  '#e9c46a', // 3  gold
  '#264653', // 4  dark teal
  '#f4a261', // 5  orange
  '#a8dadc', // 6  light blue
  '#457b9d', // 7  steel blue
  '#6d2b91', // 8  purple
  '#2dc653', // 9  green
  '#f72585', // 10 hot pink
  '#7b2d8b', // 11 dark purple
  '#ff9f1c', // 12 amber
  '#2ec4b6', // 13 cyan
  '#c77dff', // 14 lavender
  '#06d6a0', // 15 mint
];

// ─── Result type ──────────────────────────────────────────────────────────────
/**
 * @typedef {{ x:number, y:number, colorIndex:number, jump:boolean }} Stitch
 * @typedef {{ stitches:Stitch[], colors:string[], format:string,
 *             minX:number, maxX:number, minY:number, maxY:number }} StitchData
 */

// ─── Helpers ──────────────────────────────────────────────────────────────────
function base64ToUint8Array(b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function bounds(stitches) {
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const s of stitches) {
    if (s.x < minX) minX = s.x; if (s.x > maxX) maxX = s.x;
    if (s.y < minY) minY = s.y; if (s.y > maxY) maxY = s.y;
  }
  return { minX, maxX, minY, maxY };
}

// ─── DST Parser (Tajima) ──────────────────────────────────────────────────────
/**
 * Parses a Tajima DST binary (as base64 string or ArrayBuffer).
 *
 * DST encodes each stitch in exactly 3 bytes using balanced ternary for
 * X and Y displacements.  The third byte carries command flags.
 *
 * Bit layout (from pyembroidery DstReader.py — the reference implementation):
 *   decode_dx:
 *     b0 bit0(+1) bit1(-1) bit2(+9) bit3(-9)
 *     b1 bit0(+3) bit1(-3) bit2(+27) bit3(-27)
 *     b2 bit2(+81) bit3(-81)
 *   decode_dy (negated for screen coords):
 *     b0 bit7(-1) bit6(+1) bit5(-9) bit4(+9)
 *     b1 bit7(-3) bit6(+3) bit5(-27) bit4(+27)
 *     b2 bit5(-81) bit4(+81)
 *   Commands (b2 mask checks, order matters):
 *     End:          (b2 & 0xF3) === 0xF3
 *     Color change: (b2 & 0xC3) === 0xC3
 *     Jump/move:    (b2 & 0x83) === 0x83
 *
 * 1 unit = 0.1 mm
 */
export function parseDST(input) {
  const bytes = typeof input === 'string' ? base64ToUint8Array(input)
                                           : new Uint8Array(input);

  const DST_HEADER = 512;
  const stitches = [];
  let x = 0, y = 0, colorIndex = 0;

  for (let i = DST_HEADER; i + 2 < bytes.length; i += 3) {
    const b0 = bytes[i], b1 = bytes[i + 1], b2 = bytes[i + 2];

    // End of file
    if ((b2 & 0xF3) === 0xF3) break;

    // Decode X (pyembroidery decode_dx — no negation)
    let dx = 0;
    if (b0 & 0x01) dx += 1;   if (b0 & 0x02) dx -= 1;
    if (b0 & 0x04) dx += 9;   if (b0 & 0x08) dx -= 9;
    if (b1 & 0x01) dx += 3;   if (b1 & 0x02) dx -= 3;
    if (b1 & 0x04) dx += 27;  if (b1 & 0x08) dx -= 27;
    if (b2 & 0x04) dx += 81;  if (b2 & 0x08) dx -= 81;

    // Decode Y (pyembroidery decode_dy — result is negated for screen coords)
    let dy = 0;
    if (b0 & 0x80) dy -= 1;   if (b0 & 0x40) dy += 1;
    if (b0 & 0x20) dy -= 9;   if (b0 & 0x10) dy += 9;
    if (b1 & 0x80) dy -= 3;   if (b1 & 0x40) dy += 3;
    if (b1 & 0x20) dy -= 27;  if (b1 & 0x10) dy += 27;
    if (b2 & 0x20) dy -= 81;  if (b2 & 0x10) dy += 81;

    x += dx;
    y += dy;

    // Color change: bits 0,1,6,7 of b2 all set
    if ((b2 & 0xC3) === 0xC3) { colorIndex++; continue; }

    // Jump/move: bits 0,1,7 of b2 all set
    const isJump = (b2 & 0x83) === 0x83;
    stitches.push({ x, y, colorIndex, jump: isJump });
  }

  const color = (idx) => DEFAULT_THREAD_COLORS[idx % DEFAULT_THREAD_COLORS.length];
  const numColors = colorIndex + 1;
  const colors = Array.from({ length: numColors }, (_, i) => color(i));

  return { stitches, colors, format: 'DST', ...bounds(stitches) };
}

// ─── PES Parser (Brother) ─────────────────────────────────────────────────────
/**
 * Parses a Brother PES binary (base64 or ArrayBuffer).
 *
 * PES structure:
 *   #PES + version (8 bytes header)
 *   → pointer to PEC section
 *   PEC section contains:
 *     - 'LA:' label block
 *     - color table (RGB triplets)
 *     - stitch data: variable-length encoded X/Y with flags
 *
 * PES stitch encoding (PEC section):
 *   If high bit of byte 1 is set: 2-byte record (12-bit signed X, 12-bit signed Y)
 *   Otherwise: 1-byte record (7-bit signed relative move)
 *   Special values: 0xFE 0xB0 = color change, 0xFF = end
 */
export function parsePES(input) {
  const bytes = typeof input === 'string' ? base64ToUint8Array(input)
                                           : new Uint8Array(input);

  // Validate magic bytes "#PES"
  if (bytes[0] !== 0x23 || bytes[1] !== 0x50 || bytes[2] !== 0x45 || bytes[3] !== 0x53) {
    return null;
  }

  // Read PEC section pointer (UInt32 LE at offset 8)
  const pecOffset = bytes[8] | (bytes[9] << 8) | (bytes[10] << 16) | (bytes[11] << 24);

  // Read color table — located 48 bytes before end of PEC header stitch data start
  // Color table starts at pecOffset + 48 + 1 (skip the 'LA:' label area)
  const colorTableOffset = pecOffset + 48;
  const numColors = bytes[colorTableOffset] + 1;
  const colors = [];
  // PES color table contains indices into a standard thread palette
  // We use predefined RGB values for the most common Brother thread colors
  const PES_COLORS = [
    '#1a1a1a','#1a1a84','#008400','#840000','#840084','#008484','#848400',
    '#848484','#0000ff','#00ff00','#ff0000','#ff00ff','#00ffff','#ffff00',
    '#ffffff','#c8c8c8','#ffc8c8','#c8ffc8','#c8c8ff','#ffc8ff','#ffe4c8',
    '#c8e4ff','#ffd700','#ff8c00','#8b0000','#006400','#00008b','#8b008b',
    '#008b8b','#8b8b00','#2f4f4f','#696969','#708090','#4169e1','#1e90ff',
    '#87ceeb','#98fb98','#90ee90','#32cd32','#adff2f','#ffff54','#ffd700',
    '#ffa500','#ff8c00','#ff6347','#ff4500','#dc143c','#b22222','#800000',
  ];
  for (let i = 0; i < numColors; i++) {
    const idx = bytes[colorTableOffset + 1 + i];
    colors.push(PES_COLORS[idx % PES_COLORS.length] ?? '#333333');
  }

  // PEC stitch data starts at pecOffset + 528: a 512-byte PEC header block,
  // then a 16-byte stitch-block header (0x00 0x00, a 3-byte block length, the
  // "0x31 0xFF 0xF0" marker, and four 16-bit extents/hoop values).
  //
  // We also read the 24-bit little-endian stitch-block length (at +514) so we
  // know exactly where the stitch list ends. A bare 0xFF can legitimately be
  // the first byte of a long, negative stitch, so it can't reliably terminate
  // the loop on its own — the explicit end bound prevents reading into the PEC
  // thumbnail graphics that follow the stitches.
  const blockLen = bytes[pecOffset + 514] |
                   (bytes[pecOffset + 515] << 8) |
                   (bytes[pecOffset + 516] << 16);
  const stitchStart = pecOffset + 528;
  const stitchEnd = blockLen > 0
    ? Math.min(bytes.length, pecOffset + 512 + blockLen)
    : bytes.length;

  const stitches = [];
  let x = 0, y = 0, colorIndex = 0;
  let pos = stitchStart;

  // PEC delta encoding (matches pyembroidery's PecReader):
  //   short form: 1 byte, 7-bit signed (-64..63)
  //   long form:  high bit (0x80) set → 2 bytes, 12-bit signed; flags in the
  //               first byte: 0x10 = jump, 0x20 = trim
  // Crucially, X and Y are encoded INDEPENDENTLY — each is short or long on its
  // own — so a record is 2, 3, or 4 bytes. Decoding them as a fixed-size pair
  // desyncs the whole stream (the "garbled mess").
  const signed7 = (b) => (b > 63 ? b - 128 : b);
  const signed12 = (b) => { b &= 0xFFF; return b > 0x7FF ? b - 0x1000 : b; };

  while (pos < stitchEnd) {
    const v1 = bytes[pos];
    const v2 = bytes[pos + 1];
    if (v1 === undefined || v2 === undefined) break;

    // End of stitches
    if (v1 === 0xFF && v2 === 0x00) break;

    // Color change: 0xFE 0xB0 <palette index byte>
    if (v1 === 0xFE && v2 === 0xB0) {
      colorIndex++;
      pos += 3;
      continue;
    }

    let dx, dy, jump = false, trim = false;
    let p = pos;

    // X (1 or 2 bytes)
    if (v1 & 0x80) {
      if (v1 & 0x20) trim = true;
      if (v1 & 0x10) jump = true;
      dx = signed12((v1 << 8) | v2);
      p += 2;
    } else {
      dx = signed7(v1);
      p += 1;
    }

    // Y (1 or 2 bytes) — read from wherever X left off
    const yb1 = bytes[p];
    if (yb1 === undefined) break;
    if (yb1 & 0x80) {
      const yb2 = bytes[p + 1];
      if (yb2 === undefined) break;
      if (yb1 & 0x20) trim = true;
      if (yb1 & 0x10) jump = true;
      dy = signed12((yb1 << 8) | yb2);
      p += 2;
    } else {
      dy = signed7(yb1);
      p += 1;
    }

    x += dx; y += dy;
    stitches.push({ x, y, colorIndex, jump: jump || trim });
    pos = p;
  }

  return {
    stitches,
    colors: colors.length ? colors : DEFAULT_THREAD_COLORS.slice(0, Math.max(1, colorIndex + 1)),
    format: 'PES',
    ...bounds(stitches),
  };
}

// ─── Public entry point ───────────────────────────────────────────────────────
/**
 * Parse embroidery binary data (base64 string from API response).
 * Automatically selects DST or PES parser based on format string.
 *
 * @param {string} base64Data   - base64-encoded binary from conversionResult.data
 * @param {string} format       - 'dst' | 'pes' | ... (falls back to DST)
 * @returns {StitchData|null}
 */
export function parseEmbroideryFile(base64Data, format) {
  try {
    const fmt = (format || '').toLowerCase();
    if (fmt === 'pes') {
      const result = parsePES(base64Data);
      if (result && result.stitches.length > 0) return result;
    }
    // Default: DST (also used as fallback)
    const result = parseDST(base64Data);
    return result.stitches.length > 0 ? result : null;
  } catch (e) {
    console.warn('[stitchParser] Parse error:', e);
    return null;
  }
}

/**
 * Subsample a stitch array for performance.
 * Keeps all color-change boundaries and every Nth stitch in between.
 * Jump stitches are always kept (they define travel paths).
 */
export function subsampleStitches(stitches, maxPoints = 20000) {
  if (stitches.length <= maxPoints) return stitches;
  const ratio = stitches.length / maxPoints;
  const result = [];
  let lastColor = -1;
  for (let i = 0; i < stitches.length; i++) {
    const s = stitches[i];
    const colorChanged = s.colorIndex !== lastColor;
    if (colorChanged || s.jump || i % Math.ceil(ratio) === 0) {
      result.push(s);
      lastColor = s.colorIndex;
    }
  }
  return result;
}
